// React mirror of AccountService state. Screens (J9) and the call engine's UI
// consumers read account/identity through this context.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { webArgon2id } from '../identity/argon2Web.js';
import { webPasskeyProvider } from '../identity/webPasskeys.js';
import type { IdentityKeys } from '@merkury/brain/identity/keys.js';
import type { OnboardingResult } from '../onboarding/types.js';
import type { PasskeySummary } from '@merkury/protocol';
import type { RestClient } from '@merkury/brain/api/client.js';
import type { StoredAccount } from '@merkury/brain/storage/accountStore.js';
import type { KvStore } from '@merkury/brain/storage/kv.js';
import { BrainAccount } from '@merkury/brain/runtime/account.js';
import { TOKEN_ACQUISITION_BUDGET_MS } from '@merkury/brain/lib/http.js';
import { clearLocalAccountData } from '../storage/index.js';
import { clearKvStores, initKvStores, resolveDeviceKv } from '../storage/kvStores.js';
import { AccountService } from './accountService.js';
import { isCapacitorNative } from './capacitor/platform.js';
import { seedShellRestOrigin, shellRestOrigin } from './restOrigin.js';

export type AccountState =
  | { status: 'loading' }
  | { status: 'onboarding-required' }
  | { status: 'ready'; account: StoredAccount; identity: IdentityKeys }
  /**
   * This device's session was definitively rejected by the server (401/403)
   * on refresh, and every on-device recovery (stored-token retry, identity
   * key login, passkey login — see AccountService.recoverSession /
   * AccountService.getAccessToken) also failed definitively. Distinct from
   * 'onboarding-required': the identity WAS present and has just been wiped
   * (same wipe as an explicit sign-out — see accountContext.signOut and
   * screens/settings/signOutFlow.ts), so a consumer should show a "this
   * session ended" message and the restore paths (passkey / recovery
   * phrase), not treat this like a fresh install. NEVER set from a network
   * error, a timeout, or a 5xx — those are not definitive and must leave the
   * app 'ready' and browsable offline (prime directive, docs/00).
   */
  | { status: 'signed-out' };

/** True only for a definitive server rejection of a refresh/re-login attempt
 *  — never for a network failure, timeout, or 5xx (those must not sign the
 *  device out; see the `'signed-out'` AccountState variant).
 *
 *  Recognized by SHAPE, not `instanceof`: the same rejection surfaces as the
 *  brain's `AuthHttpError` or its REST client's `RestError` depending on which
 *  leg of the token chain failed, and a UI must not import either. */
function isDefinitiveAuthRejection(error: unknown): boolean {
  const status = (error as { status?: unknown } | null)?.status;
  return status === 401 || status === 403;
}

export interface AccountContextValue {
  state: AccountState;
  completeOnboarding: (result: OnboardingResult) => Promise<void>;
  /**
   * True from the moment auth completes (state flips to 'ready') until the
   * onboarding flow's post-auth permission-priming steps (mic/notifications)
   * finish. AppRoot must consult this — not a flag local to the onboarding
   * route — because AppRoot itself swaps <App/> for a freshly-keyed
   * <ReadyApp/> the instant `state.status` becomes 'ready'; a flag living
   * inside the subtree being replaced would just be destroyed by that swap
   * and never actually gate anything (field report 2026-07-17: the
   * mic/notification steps never appeared because of exactly this — see
   * OnboardingRoute in App.tsx).
   */
  onboardingFlowActive: boolean;
  setOnboardingFlowActive: (active: boolean) => void;
  /**
   * Restore the identity from a synced passkey (docs/03 §2). Resolves the
   * reconstructed keys for onboarding to finish, or undefined when passkey
   * restore isn't available for that account.
   */
  restoreWithPasskey: (userId?: string) => Promise<IdentityKeys | undefined>;
  /**
   * Best-effort prefetch of the passkey-restore options so the ceremony can
   * start with no network await inside the user gesture (Safari's transient
   * activation window). Fire-and-forget; failures fall back to fetch-in-tap.
   */
  primePasskeyRestore: () => void;
  /**
   * Drain the avatar index chosen during onboarding (once): the authed
   * runtime PATCHes it to the server via the session RestClient — the
   * service itself has no authenticated REST surface.
   */
  takePendingAvatarSeed: () => Promise<number | undefined>;
  signOut: () => Promise<void>;
  /**
   * A currently-valid JWT access token (refreshing single-flight as needed).
   * Rejects until login completes — the REST client and call engine treat that
   * as "offline for now" and retry, so it is safe to wire in eagerly.
   */
  getAccessToken: () => Promise<string>;
  /** The shared KV store (IndexedDB in the browser), for engine-side storage. */
  getKv: () => Promise<KvStore>;
  /**
   * Persist / clear the local password authKey (2FA proof) after the settings
   * password flow changes it, so silent re-login attaches the current proof.
   */
  setStoredAuthKey: (authKey: Uint8Array | undefined) => Promise<void>;
  /**
   * The credential-guarded account ceremonies (docs/03 §2, §3a, §3b), run by
   * the brain. They take the authenticated RestClient because only the authed
   * runtime has one — the provider itself is mounted before any session exists.
   */
  credentials: {
    setPassword: (rest: RestClient, password: string, terminateOthers: boolean) => Promise<void>;
    removePassword: (rest: RestClient, terminateOthers: boolean) => Promise<void>;
    deleteAccount: (rest: RestClient, password?: string) => Promise<void>;
    primeAddPasskey: (rest: RestClient) => Promise<void>;
    addPasskey: (
      rest: RestClient,
      label?: string,
    ) => Promise<{ passkey: PasskeySummary; restorable: boolean } | null>;
  };
}

const AccountContext = createContext<AccountContextValue | undefined>(undefined);

export interface AccountProviderProps {
  children: ReactNode;
  /** Test seam: storage + fetch injection (jsdom has no IndexedDB). */
  kv?: KvStore;
  fetchFn?: typeof fetch;
}

export function AccountProvider({ children, kv, fetchFn }: AccountProviderProps): ReactNode {
  const [state, setState] = useState<AccountState>({ status: 'loading' });
  const [onboardingFlowActive, setOnboardingFlowActive] = useState(false);
  const serviceRef = useRef<Promise<AccountService> | undefined>(undefined);
  const kvRef = useRef<Promise<KvStore> | undefined>(undefined);
  // Fire the 'signed-out' transition (+ its wipe) at most once per definitive
  // rejection — getAccessToken() is called concurrently from many places
  // (REST client, call engine, session revival) and they'd otherwise all race
  // to wipe/setState at once.
  const handledSessionLossRef = useRef(false);

  const getKv = useCallback((): Promise<KvStore> => {
    // Platform storage: Keystore-backed KV inside the Capacitor WebView
    // (adapters stay out of the web bundle via dynamic import), IndexedDB
    // otherwise (storage/kvStores.ts owns that branch so there is exactly one
    // copy of it). The `kv` prop overrides both (tests).
    kvRef.current ??= (async (): Promise<KvStore> => {
      const resolved = kv ?? (await resolveDeviceKv());
      // Boot wiring (docs/09 §10): one-time legacy-localStorage → KV import,
      // then hydrate the favorites/aliases/call-log mirrors. Fire-and-forget —
      // this is the earliest point the app resolves its on-device KV, and
      // gating on it here would delay first paint; the mirrors just read
      // empty (no spinner) until it resolves, same as a fresh device.
      void initKvStores(resolved);
      return resolved;
    })();
    return kvRef.current;
  }, [kv]);

  const getService = useCallback((): Promise<AccountService> => {
    serviceRef.current ??= (async () => {
      // Secret wrapping: Android Keystore cipher on native (a CryptoKey can't
      // cross the Capacitor bridge), WebCrypto default elsewhere.
      const cipher = isCapacitorNative()
        ? (await import('./capacitor/index.js')).createNativeSecretCipher()
        : undefined;
      const store = await getKv();
      // Auth is the first REST any launch does and the gate on all the rest —
      // no token, no friends list and no signaling race — so it must follow the
      // endpoint list from the FIRST byte rather than the build-time host a
      // censor can freeze (docs/04 §2). The router is shared with the session's
      // REST client and the healing `/endpoints` poll (restOrigin.ts); seeding
      // it from the previous session's winner is best-effort and never blocks
      // boot on storage.
      await seedShellRestOrigin(store);
      const brain = new BrainAccount({
        kv: store,
        ...(cipher ? { secretCipher: cipher } : {}),
        baseUrl: shellRestOrigin(),
        // WASM Argon2id and the browser/Capacitor WebAuthn ceremony: the two
        // platform seams the brain refuses to import for itself.
        argon2: webArgon2id,
        passkeys: webPasskeyProvider,
        // Sessions created on the Capacitor app list as 'android'.
        sessionKind: isCapacitorNative() ? 'android' : 'web',
        // The userId lives inside the stored account here; only the iOS boot
        // config needs it separately.
        persistUserId: () => undefined,
        // Onboarding must complete on a hostile network (prime directive).
        offlineProvision: true,
        // Favorites/aliases/call-log now live in the SAME KV `store.wipe()`
        // just cleared (docs/09 §10) — clearLocalAccountData only remains for
        // the legacy-localStorage mirrors (migration hygiene: a stale copy
        // must not exist to re-import on the next boot) and the still-
        // localStorage-backed contacts/server cache. The KV wipe runs BEFORE
        // this (BrainAccount.dropAllLocalState ordering), so also reset this
        // process's in-memory favorites/aliases/call-log mirrors here — a
        // process-lifetime singleton wiped state alone would not clear.
        clearLocalData: async () => {
          clearLocalAccountData();
          await clearKvStores();
        },
        tokenBudgetMs: TOKEN_ACQUISITION_BUDGET_MS,
        // Well under the token budget: waiting for another tab's rotation must
        // not by itself exhaust a caller's whole budget.
        lockWaitMs: 2_000,
        ...(fetchFn ? { fetchFn } : {}),
      });
      return new AccountService(brain);
    })();
    return serviceRef.current;
  }, [getKv, fetchFn]);

  const getAccessToken = useCallback(async (): Promise<string> => {
    const service = await getService();
    try {
      return await service.getAccessToken();
    } catch (err) {
      // By the time this throws, AccountService has already exhausted every
      // on-device recovery for this refresh (stored-token retry, identity key
      // login, passkey login) — this is not "a single API 401 mid-flight",
      // it's the final, definitive outcome. Still: only 401/403 counts. A
      // network error, timeout, or 5xx must leave the app 'ready' — the
      // backend being unreachable is never a reason to sign out (prime
      // directive, docs/00: offline-first).
      if (isDefinitiveAuthRejection(err) && !handledSessionLossRef.current) {
        handledSessionLossRef.current = true;
        // Same wipe as an explicit sign-out (screens/settings/signOutFlow.ts,
        // AccountSettingsScreen's LogOutSection): no separate partial-wipe
        // policy for this path.
        await service.signOut().catch(() => undefined);
        setState({ status: 'signed-out' });
      }
      throw err;
    }
  }, [getService]);

  useEffect(() => {
    let cancelled = false;
    void getService()
      .then((service) => service.load())
      .then((loaded) => {
        if (cancelled) return;
        setState(
          loaded
            ? { status: 'ready', account: loaded.account, identity: loaded.identity }
            : { status: 'onboarding-required' },
        );
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'onboarding-required' });
      });
    return () => {
      cancelled = true;
    };
  }, [getService]);

  const completeOnboarding = useCallback(
    async (result: OnboardingResult) => {
      const service = await getService();
      const account = await service.completeOnboarding(result);
      // Fresh session: a later definitive rejection must be able to fire again.
      handledSessionLossRef.current = false;
      setState({ status: 'ready', account, identity: result.identity });
    },
    [getService],
  );

  const restoreWithPasskey = useCallback(
    async (userId?: string): Promise<IdentityKeys | undefined> => {
      const service = await getService();
      return service.restoreWithPasskey(userId);
    },
    [getService],
  );

  const primePasskeyRestore = useCallback((): void => {
    void getService()
      .then((service) => service.primePasskeyRestore())
      .catch(() => undefined);
  }, [getService]);

  const takePendingAvatarSeed = useCallback(
    async (): Promise<number | undefined> => (await getService()).takePendingAvatarSeed(),
    [getService],
  );

  const signOut = useCallback(async () => {
    const service = await getService();
    await service.signOut();
    handledSessionLossRef.current = false;
    setState({ status: 'onboarding-required' });
  }, [getService]);

  const setStoredAuthKey = useCallback(
    async (authKey: Uint8Array | undefined) => {
      const service = await getService();
      await service.setStoredAuthKey(authKey);
    },
    [getService],
  );

  const credentials = useMemo(
    () => ({
      setPassword: async (rest: RestClient, password: string, terminateOthers: boolean) =>
        (await getService()).setPassword(rest, password, terminateOthers),
      removePassword: async (rest: RestClient, terminateOthers: boolean) =>
        (await getService()).removePassword(rest, terminateOthers),
      deleteAccount: async (rest: RestClient, password?: string) =>
        (await getService()).deleteAccount(rest, password),
      primeAddPasskey: async (rest: RestClient) => (await getService()).primeAddPasskey(rest),
      addPasskey: async (rest: RestClient, label?: string) =>
        (await getService()).addPasskey(rest, label),
    }),
    [getService],
  );

  // The Android native-UI bridge used to be installed here (it had to exist
  // before an account did, because the Compose onboarding drove every identity
  // op through it). Since B3-android the React tree never runs inside the
  // Android WebView: `brain.html` is that WebView's only document and the
  // unified brain dispatcher serves those methods (docs/09 §2/§3). This
  // provider is web-only now.

  const value = useMemo(
    () => ({
      state,
      completeOnboarding,
      onboardingFlowActive,
      setOnboardingFlowActive,
      restoreWithPasskey,
      primePasskeyRestore,
      takePendingAvatarSeed,
      signOut,
      getAccessToken,
      getKv,
      setStoredAuthKey,
      credentials,
    }),
    [
      state,
      completeOnboarding,
      onboardingFlowActive,
      restoreWithPasskey,
      primePasskeyRestore,
      takePendingAvatarSeed,
      signOut,
      getAccessToken,
      getKv,
      setStoredAuthKey,
      credentials,
    ],
  );
  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
}

export function useAccount(): AccountContextValue {
  const value = useContext(AccountContext);
  if (!value) throw new Error('useAccount must be used inside <AccountProvider>');
  return value;
}
